/**
 * @file Redirect.tsx
 * @description This file defines the Redirect component, which serves as a temporary landing page
 * during the MSAL (Microsoft Authentication Library) redirect-based login flow.
 */

import React, { useEffect } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import { Spinner, SpinnerSize } from "@fluentui/react";
import { useTranslation } from "react-i18next";

/**
 * @component Redirect
 * @description This component is displayed after a user authenticates with an external provider
 * and is redirected back to the application. It waits for the MSAL library to confirm that the
 * user is authenticated and then navigates them to the application's home page.
 * It provides visual feedback to the user in the form of a loading spinner during this process.
 */
export const Redirect: React.FC = () => {
    const { instance } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const navigate = useNavigate();
    const { t } = useTranslation();

    /**
     * @hook useEffect
     * @description This effect monitors the `isAuthenticated` status from MSAL.
     * Once the status becomes `true`, it means the authentication process is complete,
     * and it navigates the user to the root path ("/") of the application.
     */
    useEffect(() => {
        console.log(`[Redirect.tsx] isAuthenticated effect fired. isAuthenticated: ${isAuthenticated}`);
        // Only navigate away once MSAL confirms the user is authenticated.
        if (isAuthenticated) {
            console.log("[Redirect.tsx] isAuthenticated is true, navigating to '/'");
            navigate("/");
        }
    }, [isAuthenticated, navigate]);

    // Render a loading indicator while waiting for the authentication to complete.
    return (
        <div
            style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100vh"
            }}
        >
            <Spinner size={SpinnerSize.large} label={t("loginComponent.processingLogin")} />
        </div>
    );
};
